import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Download, PlayCircle, Lock, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { studentApi, isStudentLoggedIn } from "../lib/studentAuth";
import PromoCountdown from "../components/PromoCountdown";
import ShareWithFriend from "../components/ShareWithFriend";

const STORAGE_KEY = "mw_dbd_unlocked";

const MiniCourse = () => {
    const [course, setCourse] = useState(null);
    const [params] = useSearchParams();
    const [unlocked, setUnlocked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeLesson, setActiveLesson] = useState(null);

    useEffect(() => {
        api.get("/mini-course").then((r) => {
            setCourse(r.data);
            setActiveLesson(r.data.lessons[0]);
        });
        // Unlock priority: (1) logged-in student with this course, (2) legacy localStorage, (3) ?paid=1 fresh redirect
        const wasUnlocked = localStorage.getItem(STORAGE_KEY) === "1";
        const justPaid = params.get("session_id") || params.get("paid") === "1";
        if (wasUnlocked || justPaid) {
            setUnlocked(true);
            localStorage.setItem(STORAGE_KEY, "1");
            if (justPaid) toast.success("Welcome to The Daily Battle Drill. You have full access.");
        }
        // Cross-device check: if the user is logged in and owns this course, unlock.
        if (isStudentLoggedIn()) {
            studentApi.get("/students/me").then((r) => {
                const ids = r.data?.course_ids || [];
                if (ids.includes("daily-battle-drill")) {
                    setUnlocked(true);
                    localStorage.setItem(STORAGE_KEY, "1");
                }
            }).catch(() => {});
        }
    }, [params]);

    const startCheckout = async () => {
        setLoading(true);
        try {
            const r = await api.post("/payments/checkout/session", {
                package_id: "daily-battle-drill",
                origin_url: window.location.origin,
            });
            if (r.data?.url) window.location.href = r.data.url;
            else toast.error("Checkout couldn't start.");
        } catch {
            toast.error("Checkout failed to start.");
        } finally {
            setLoading(false);
        }
    };

    if (!course) return <div className="bg-black min-h-[60vh]" />;
    const backend = process.env.REACT_APP_BACKEND_URL;

    return (
        <div className="bg-black" data-testid="mini-course-page">
            {/* PROMO HERO IMAGE — full-width banner above the sales pitch */}
            <section className="bg-black border-b border-white/10" data-testid="mini-promo-banner">
                <div className="max-w-6xl mx-auto px-4 md:px-6 pt-10 md:pt-12 pb-2">
                    <img
                        src={`${backend}/api/mini-course/promo-hero`}
                        alt="The Daily Battle Drill — full product mockup"
                        className="w-full h-auto block border border-white/10 shadow-[0_0_80px_-20px_rgba(185,28,28,0.5)]"
                        loading="eager"
                        data-testid="mini-promo-image"
                    />
                </div>
            </section>

            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-red-700/20 blur-3xl rounded-full pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-center" id="hero-buy">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" /> Holiday Sale · $7 through Christmas · 96% off
                        </div>
                        <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-5xl md:text-7xl lg:text-8xl" data-testid="mini-headline">
                            The Daily <br />
                            <span className="text-red-600">Battle Drill</span>
                        </h1>
                        <p className="mt-6 font-display text-xl md:text-2xl uppercase tracking-tight text-white/80">{course.tagline}</p>
                        <p className="mt-6 max-w-xl text-white/70 leading-relaxed">{course.promise}</p>

                        {!unlocked && (
                            <div className="mt-8" data-testid="mini-countdown-wrap">
                                <PromoCountdown />
                            </div>
                        )}

                        {!unlocked ? (
                            <button
                                onClick={startCheckout}
                                disabled={loading}
                                className="mt-8 inline-flex items-center gap-4 px-8 py-5 bg-red-700 hover:bg-red-600 text-white font-display tracking-[0.12em] uppercase transition-colors disabled:opacity-50 shadow-[0_0_60px_-15px_rgba(185,28,28,0.8)]"
                                data-testid="mini-buy"
                            >
                                <Sparkles size={20} />
                                <span className="flex items-baseline gap-3">
                                    <span className="text-sm">Get instant access</span>
                                    <span className="relative text-white/55 text-lg line-through decoration-2 decoration-white/80">$197</span>
                                    <span className="font-display text-3xl md:text-4xl leading-none">${course.price_usd}</span>
                                </span>
                                {loading && <span className="text-sm">…</span>}
                            </button>
                        ) : (
                            <a href="#lessons" className="mt-10 btn-primary inline-flex" data-testid="mini-watch-now">
                                <PlayCircle size={18} /> Watch Lesson 1
                            </a>
                        )}
                        <p className="mt-4 text-xs text-white/40">One-time payment · Instant access · Lifetime</p>
                        {!unlocked && (
                            <p className="mt-2 text-xs text-white/40" data-testid="mini-login-hint">
                                Already bought it on another device?{" "}
                                <Link to="/course/login" className="text-red-500 hover:text-red-400 underline underline-offset-4" data-testid="mini-login-link">
                                    Log in to unlock →
                                </Link>
                            </p>
                        )}
                    </div>

                    <div className="lg:col-span-5">
                        <div className="border-2 border-red-700/50 bg-black p-8 shadow-[0_0_80px_-20px_rgba(185,28,28,0.6)]">
                            <div className="font-stencil text-[10px] tracking-widest text-red-600">WHAT YOU GET</div>
                            <ul className="mt-5 space-y-3">
                                {course.what_you_get.map((w, i) => (
                                    <li key={i} className="flex items-start gap-3 text-white/85 text-sm leading-relaxed">
                                        <Check size={18} className="text-red-600 mt-0.5 flex-shrink-0" /> {w}
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-6 pt-6 border-t border-white/10 flex items-baseline justify-between">
                                <div className="font-display text-5xl text-white">${course.price_usd}</div>
                                <div className="text-xs text-white/40">One-time payment</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════ MIDDLE: WHY THIS COURSE — VIDEO ═══════════════ */}
            <section className="bg-[#050505] py-20 md:py-28 border-b border-white/10 relative overflow-hidden" data-testid="mini-mid-video">
                <div className="absolute inset-0 opacity-25 pointer-events-none bg-[radial-gradient(ellipse_at_center,rgba(185,28,28,0.35),transparent_60%)]" />
                <div className="relative max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="font-stencil text-[11px] tracking-[0.3em] text-red-600 mb-4">[ WHY I BUILT THIS ]</div>
                        <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter leading-[0.95]">
                            Hear it from <span className="text-red-600">me.</span>
                        </h2>
                        <p className="mt-6 text-white/70 leading-relaxed">
                            60 seconds. No fluff. Why I built The Daily Battle Drill — and what it&apos;s actually going to do for you when you start running it.
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="border-2 border-red-700 bg-black overflow-hidden shadow-[0_0_120px_-30px_rgba(185,28,28,0.7)] max-w-sm mx-auto">
                            <div className="relative aspect-[9/16] bg-[#050505]">
                                <video
                                    src="/dbd-sales-video.mp4"
                                    poster="/dbd-sales-video-poster.jpg"
                                    controls
                                    playsInline
                                    preload="metadata"
                                    className="w-full h-full object-cover"
                                    data-testid="mini-mid-video-player"
                                >
                                    Sorry, your browser can&apos;t play this video.
                                </video>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════ THIS IS FOR YOU IF ═══════════════ */}
            <section className="py-20 md:py-28 border-b border-white/10" data-testid="mini-for-you">
                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                    <div className="text-center mb-12">
                        <div className="font-stencil text-[11px] tracking-[0.3em] text-red-600 mb-4">[ KNOW THIS BEFORE YOU BUY ]</div>
                        <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95]">
                            This is <span className="text-red-600">for you</span> if...
                        </h2>
                    </div>
                    <ul className="space-y-4 max-w-3xl mx-auto">
                        {[
                            "You feel trapped in depression, anxiety, or mental chaos.",
                            "You struggle with negative or intrusive thoughts that won't stop.",
                            "You wake up already feeling defeated before the day even begins.",
                            "You know what God's Word says, but you don't know how to apply it in the middle of the battle.",
                            "You're tired of reacting to your thoughts instead of leading them.",
                            "You want a simple, practical strategy — not just more motivation.",
                            "You're ready to stop surviving and start fighting back.",
                            "You want to build a biblical battle plan for your mind that you can use every single day.",
                        ].map((line, i) => (
                            <li key={i} className="flex gap-4 items-start border-l-2 border-red-700/60 bg-[#0a0a0a] p-5 hover:border-red-600 transition-colors" data-testid={`mini-for-you-item-${i}`}>
                                <Check size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                                <span className="text-white/85 leading-relaxed">{line}</span>
                            </li>
                        ))}
                    </ul>
                    {!unlocked && (
                        <div className="text-center mt-12">
                            <button
                                onClick={startCheckout}
                                disabled={loading}
                                className="inline-flex items-center gap-4 px-8 py-5 bg-red-700 hover:bg-red-600 text-white font-display tracking-[0.12em] uppercase transition-colors disabled:opacity-50 shadow-[0_0_60px_-15px_rgba(185,28,28,0.8)]"
                                data-testid="mini-for-you-buy"
                            >
                                <Sparkles size={20} />
                                <span className="flex items-baseline gap-3">
                                    <span className="text-sm">Get the drill</span>
                                    <span className="text-white/55 text-lg line-through decoration-2 decoration-white/80">$197</span>
                                    <span className="font-display text-3xl md:text-4xl leading-none">${course.price_usd}</span>
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* ═══════════════ FAQ ═══════════════ */}
            <section className="bg-[#050505] py-20 md:py-28 border-b border-white/10" data-testid="mini-faq">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="text-center mb-12">
                        <div className="font-stencil text-[11px] tracking-[0.3em] text-red-600 mb-4">[ FAQ ]</div>
                        <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95]">
                            Frequently <br /><span className="text-red-600">asked.</span>
                        </h2>
                    </div>
                    <div className="space-y-3">
                        {[
                            { q: "Is this course for Christians only?", a: "The course is built on biblical truth and points people to Jesus Christ. Anyone can benefit from the practical mental health principles, but the foundation is Scripture." },
                            { q: "How long is the course?", a: "The training is short and practical. You can complete it in about 15–20 minutes, then immediately begin applying the Battle Drill to your daily life." },
                            { q: "Is this therapy or counseling?", a: "No. This course is not a replacement for professional counseling, psychiatric care, or medical treatment. It's a practical framework designed to help you develop healthier thought patterns while encouraging you to seek professional help when appropriate." },
                            { q: "Will this cure my depression or anxiety?", a: "No one can honestly promise that. What this course will do is give you a biblical and practical strategy to help you interrupt destructive thinking, build healthier mental habits, and begin fighting back with intentionality." },
                            { q: "I've struggled for years. Is this still for me?", a: "Absolutely. I created this because I know what it's like to feel trapped. Change often happens one thought, one choice, and one day at a time." },
                            { q: "What will I receive?", a: "You'll receive instant access to the Battle Plan training, downloadable resources, and a simple daily framework you can begin using immediately." },
                        ].map((item, i) => (
                            <details
                                key={i}
                                className="group border border-white/10 bg-black hover:border-red-700/50 transition-colors"
                                data-testid={`mini-faq-item-${i}`}
                            >
                                <summary className="flex items-center justify-between gap-4 p-6 cursor-pointer list-none">
                                    <span className="font-display text-lg md:text-xl uppercase tracking-tight text-white pr-4">{item.q}</span>
                                    <span className="font-display text-3xl text-red-600 group-open:rotate-45 transition-transform select-none flex-shrink-0">+</span>
                                </summary>
                                <div className="px-6 pb-6 -mt-2 text-white/70 leading-relaxed border-t border-white/5 pt-4">
                                    {item.a}
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* LESSONS */}
            <section id="lessons" className="py-20 md:py-24 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                        <div>
                            <div className="font-stencil text-xs tracking-widest text-red-600 mb-3">4 LESSONS · INSTANT ACCESS</div>
                            <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter">Your Curriculum</h2>
                        </div>
                        {unlocked && (
                            <a href={`${backend}${course.workbook_url}`} target="_blank" rel="noopener noreferrer" className="btn-ghost text-sm" data-testid="mini-workbook">
                                <Download size={16} /> Download Workbook PDF
                            </a>
                        )}
                    </div>

                    {unlocked && activeLesson && (
                        <div className="mb-8 border-2 border-red-700/40 bg-[#050505]" data-testid="mini-player-wrap">
                            <video
                                key={activeLesson.id}
                                src={`${backend}${activeLesson.video_url}`}
                                poster={`${backend}${activeLesson.poster_url}`}
                                controls
                                playsInline
                                className="w-full h-auto block"
                                data-testid="mini-player"
                            />
                            <div className="p-5 bg-black border-t border-white/10">
                                <div className="font-stencil text-[10px] tracking-widest text-red-500">LESSON {String(activeLesson.number).padStart(2, "0")}</div>
                                <div className="mt-1 font-display text-2xl uppercase tracking-tight">{activeLesson.title}</div>
                            </div>
                        </div>
                    )}

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                        {course.lessons.map((l) => {
                            const isActive = activeLesson?.id === l.id;
                            return (
                                <button
                                    type="button"
                                    key={l.id}
                                    disabled={!unlocked}
                                    onClick={() => setActiveLesson(l)}
                                    className={`text-left p-6 ${isActive && unlocked ? "bg-red-700/15" : "bg-black"} ${unlocked ? "hover:bg-[#0a0a0a]" : "opacity-60 cursor-not-allowed"} transition-colors`}
                                    data-testid={`mini-lesson-${l.id}`}
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        {unlocked ? <PlayCircle size={20} className="text-red-600" strokeWidth={1.5} /> : <Lock size={18} className="text-white/40" />}
                                        <span className="font-display text-white/15 text-3xl">0{l.number}</span>
                                    </div>
                                    <div className="font-display text-base uppercase tracking-tight leading-tight">{l.title}</div>
                                </button>
                            );
                        })}
                    </div>

                    {!unlocked && (
                        <div className="mt-10 text-center">
                            <button onClick={startCheckout} disabled={loading} className="btn-primary inline-flex" data-testid="mini-buy-2">
                                <Sparkles size={18} /> Unlock All 4 Lessons — ${course.price_usd}
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* FOR YOU IF */}
            <section className="py-20 md:py-24">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                    <div className="font-stencil text-xs tracking-widest text-red-600 mb-3">THIS COURSE IS FOR YOU IF</div>
                    <h3 className="font-display text-3xl md:text-5xl uppercase tracking-tighter leading-[0.95]">You're tired of <span className="text-red-600">losing</span> the same battle.</h3>
                    <ul className="mt-12 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 text-left">
                        {course.for_you_if.map((t, i) => (
                            <li key={i} className="bg-black p-6 flex items-start gap-3">
                                <ShieldCheck size={18} className="text-red-600 mt-1 flex-shrink-0" /> <span className="text-white/80 text-sm leading-relaxed">{t}</span>
                            </li>
                        ))}
                    </ul>
                    {!unlocked && (
                        <button onClick={startCheckout} disabled={loading} className="mt-12 btn-primary inline-flex" data-testid="mini-buy-3">
                            <Sparkles size={18} /> Get Instant Access — ${course.price_usd}
                        </button>
                    )}
                    <p className="mt-8 text-white/50 text-sm">
                        Looking for the deeper, long-form version? <Link to="/courses" className="text-red-500 hover:text-red-400">See the full BattleFront →</Link>
                    </p>
                    {unlocked && (
                        <div className="mt-12 text-left" data-testid="mini-share-wrap">
                            <ShareWithFriend />
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default MiniCourse;
